import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { RecordCard, RecordTableToolbar, recordTablePagination } from '@client/components/_ui/display/RecordPrimitives';
import { TonePill } from '@client/components/_ui/display/TonePill';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { AddMembersDrawer } from '@client/components/repair-networks/AddMembersDrawer';
import {
    CoverageSummary,
    formatSupplierArea,
    formatSupplierLocation,
} from '@client/components/repair-networks/CoverageSummary';
import { type MemberRow, MembershipDrawer } from '@client/components/repair-networks/MembershipDrawer';
import { RepairerCapabilityChips } from '@client/components/suppliers/RepairerCapabilityChips';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { AU_STATES } from '@shared/constants/business/location';
import type { RepairerCapabilityCode } from '@shared/constants/business/repairer-capability';
import { createFileRoute } from '@tanstack/react-router';
import { Input, Select, Tag } from 'antd';
import { useState } from 'react';
import { z } from 'zod';
import { SUPPLIER_DETAILS_ROUTE, useCanManageRepairNetworks, useNetworkRecord } from './network-record-data';

const searchSchema = z.object({
    coverage: z.enum(['postcodes', 'radius', 'none']).optional().catch(undefined),
});

export const Route = createFileRoute(
    '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/members',
)({
    validateSearch: searchSchema,
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <MembersTab />
        </PageController>
    );
}

type MembersInput = RouterInputs['repairNetwork']['listRepairNetworkMembers'];
// Coverage lives in the URL (the Overview's "Show them" link deep-links to it); the rest is local.
type MembersQueryParams = Omit<MembersInput, 'repairNetworkId' | 'coverage'>;

const columnHelper = createColumnHelper<MemberRow, NonNullable<MembersInput['orderBy']>>();

function MembersTab() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { id } = Route.useParams();
    const { coverage: coverageFromUrl } = Route.useSearch();
    const { data: network } = useNetworkRecord(id);
    const canEdit = useCanManageRepairNetworks();

    const [params, setParams] = useState<MembersQueryParams>({
        limit: 20,
        offset: 0,
        orderBy: { name: 'asc' },
    });
    // Reset during render so URL changes (including history navigation) cannot
    // issue a request combining the new coverage with the previous page offset.
    const [previousCoverage, setPreviousCoverage] = useState(coverageFromUrl);
    if (previousCoverage !== coverageFromUrl) {
        setPreviousCoverage(coverageFromUrl);
        setParams((previous) => ({ ...previous, offset: 0 }));
    }
    const setCoverage = (coverage: MembersInput['coverage']) =>
        navigate({
            to: '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/members',
            params: { orgId, id },
            search: coverage ? { coverage } : {},
            replace: true,
        });
    const [searchText, setSearchText] = useState('');
    const [editing, setEditing] = useState<MemberRow | null>(null);
    const [isAddOpen, setIsAddOpen] = useState(false);

    const membersQuery = trpc.repairNetwork.listRepairNetworkMembers.useQuery({
        ...params,
        offset: previousCoverage === coverageFromUrl ? params.offset : 0,
        coverage: coverageFromUrl,
        repairNetworkId: id,
    });
    const remove = trpc.repairNetwork.removeRepairNetworkMember.useMutation();
    const hasActiveFilters =
        !!params.search ||
        !!params.state ||
        !!coverageFromUrl ||
        params.isRingFenced !== undefined ||
        params.isActive !== undefined;

    const invalidate = () => {
        void membersQuery.refetch();
        void trpcUtils.repairNetwork.getRepairNetwork.invalidate({ id });
        void trpcUtils.repairNetwork.listRepairNetworks.invalidate();
    };

    const tableProps = usePrismaTable({
        rowKey: (row) => row.supplierId.toString(),
        data: membersQuery.data?.rows || [],
        total: membersQuery.data?.total || 0,
        loading: membersQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        pagination: recordTablePagination,
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No members yet"
                noRecordsDescription="Add repairers to this network and set where each one is offered."
                noMatchesTitle="No members match"
                noMatchesDescription="Try another name, suburb or postcode, or clear the filters."
            />
        ),
        columns: [
            columnHelper.accessor('supplier', {
                id: 'repairer',
                header: 'Repairer',
                sortable: 'name',
                cell: ({ row }) => (
                    <div className="flex min-w-0 flex-col">
                        <span className="font-semibold">{row.supplier.name}</span>
                        <span className="text-subtle-foreground text-xs">{formatSupplierArea(row.supplier)}</span>
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'capability',
                header: 'Capability',
                cell: ({ row }) => (
                    <RepairerCapabilityChips codes={row.supplier.capabilityCodes as RepairerCapabilityCode[]} />
                ),
            }),
            columnHelper.display({
                id: 'coverage',
                header: 'Coverage',
                cell: ({ row }) => (
                    <CoverageSummary
                        coverage={row}
                        from={formatSupplierLocation(row.supplier)}
                        hasLocation={row.hasLocation}
                    />
                ),
            }),
            columnHelper.display({
                id: 'ringFenced',
                header: 'Ring-fenced',
                width: 120,
                cell: ({ row }) =>
                    row.isRingFenced ? (
                        <Tag variant="outlined" className="m-0">
                            Ring-fenced
                        </Tag>
                    ) : (
                        <span className="text-subtle-foreground">—</span>
                    ),
            }),
            columnHelper.display({
                id: 'status',
                header: 'Status',
                width: 100,
                cell: ({ row }) =>
                    row.isActive ? <TonePill tone="ok">Active</TonePill> : <TonePill tone="neutral">Inactive</TonePill>,
            }),
            columnHelper.actions({
                width: 1,
                cell: ({ row }) => (
                    <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
                        <Button.OpenInNewTab to={SUPPLIER_DETAILS_ROUTE} params={{ orgId, id: row.supplierId }} />
                        {canEdit ? (
                            <>
                                <Button type="link" size="small" onClick={() => setEditing(row)}>
                                    Edit
                                </Button>
                                <Confirm
                                    title={`Remove ${row.supplier.name}?`}
                                    description="The repairer stops being offered through this network. Its other memberships are unaffected."
                                    okText="Remove"
                                    okDanger
                                    onOk={async () => {
                                        try {
                                            await remove.mutateAsync({
                                                repairNetworkId: id,
                                                supplierId: row.supplierId,
                                            });
                                            message.success('Removed from the network.');
                                            invalidate();
                                        } catch (error) {
                                            errorMessage.show(error);
                                        }
                                    }}
                                >
                                    <Button type="link" size="small" danger>
                                        Remove
                                    </Button>
                                </Confirm>
                            </>
                        ) : null}
                    </div>
                ),
            }),
        ],
    });

    const setFilter = (patch: Partial<MembersQueryParams>) => setParams((prev) => ({ ...prev, ...patch, offset: 0 }));

    return (
        <RecordCard padded={false}>
            <RecordTableToolbar>
                <Input.Search
                    placeholder="Search by repairer, suburb or postcode"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) => setFilter({ search: value.trim() || undefined })}
                />
                <Select
                    aria-label="State"
                    placeholder="State"
                    allowClear
                    className="w-28"
                    value={params.state}
                    onChange={(state: MembersInput['state']) => setFilter({ state })}
                    options={AU_STATES.map((value) => ({ value, label: value }))}
                />
                <Select
                    aria-label="Coverage"
                    placeholder="Coverage"
                    allowClear
                    className="w-40"
                    value={coverageFromUrl}
                    onChange={(coverage: MembersInput['coverage']) => setCoverage(coverage)}
                    options={[
                        { value: 'postcodes', label: 'Postcodes' },
                        { value: 'radius', label: 'Radius' },
                        { value: 'none', label: 'Not set' },
                    ]}
                />
                <Select
                    aria-label="Ring-fenced"
                    placeholder="Ring-fenced"
                    allowClear
                    className="w-36"
                    value={params.isRingFenced === undefined ? undefined : params.isRingFenced ? 'yes' : 'no'}
                    onChange={(value: 'yes' | 'no' | undefined) =>
                        setFilter({ isRingFenced: value === undefined ? undefined : value === 'yes' })
                    }
                    options={[
                        { value: 'yes', label: 'Ring-fenced' },
                        { value: 'no', label: 'Not ring-fenced' },
                    ]}
                />
                <Select
                    aria-label="Status"
                    placeholder="Status"
                    allowClear
                    className="w-32"
                    value={params.isActive === undefined ? undefined : params.isActive ? 'active' : 'inactive'}
                    onChange={(value: 'active' | 'inactive' | undefined) =>
                        setFilter({ isActive: value === undefined ? undefined : value === 'active' })
                    }
                    options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                    ]}
                />
                <span className="ml-auto text-subtle-foreground text-xs">
                    {network
                        ? `${network.memberCount} ${network.memberCount === 1 ? 'member' : 'members'}${network.coverage.notSet ? ` · ${network.coverage.notSet} without coverage` : ''}`
                        : ''}
                </span>
                {canEdit ? (
                    <Button.Add label="Add members" variant="primary" onClick={() => setIsAddOpen(true)} />
                ) : null}
            </RecordTableToolbar>
            {membersQuery.error ? (
                <div className="p-4">
                    <InlineQueryError compact error={membersQuery.error} onRetry={() => void membersQuery.refetch()} />
                </div>
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={(row) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate({ to: SUPPLIER_DETAILS_ROUTE, params: { orgId, id: row.supplierId } }, event);
                        },
                    })}
                />
            )}

            <MembershipDrawer
                open={editing !== null}
                networkName={network?.name ?? ''}
                member={editing}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    invalidate();
                }}
            />
            <AddMembersDrawer
                open={isAddOpen}
                repairNetworkId={id}
                networkName={network?.name ?? ''}
                onClose={() => setIsAddOpen(false)}
                onAdded={() => {
                    setIsAddOpen(false);
                    invalidate();
                }}
            />
        </RecordCard>
    );
}
